/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * API Error Handling Utilities
 * ============================================================================
 *
 * Centralized error handling, transformation, and display utilities.
 *
 * ============================================================================
 */

import { ApiError } from './types';

/**
 * Translation function type (from react-i18next)
 */
type TFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * Backend error message patterns mapped to translation keys
 * These patterns match common backend error messages and map them to i18n keys
 */
export const BACKEND_ERROR_PATTERNS: Array<{
  pattern: RegExp;
  key: string;
  extractParams?: (match: RegExpMatchArray) => Record<string, unknown>;
}> = [
  // =========================================================================
  // Subscription/Module errors
  // =========================================================================
  {
    // Matches: "Current user count 700 exceeds limit for tier CRM Pro (max: 100)"
    // Matches: "User count 50 exceeds limit for tier STARTER (max: 10). Consider upgrading..."
    pattern: /(?:Current )?user count (\d+) exceeds limit for tier (.+?) \(max: (\d+)\)/i,
    key: 'backendErrors.userCountExceedsLimit',
    extractParams: (match) => ({ count: match[1], tier: match[2], max: match[3] }),
  },
  {
    // Matches: "User count 577 is invalid for tier HR Pro"
    pattern: /User count (\d+) is invalid for tier (.+)/i,
    key: 'backendErrors.userCountInvalidForTier',
    extractParams: (match) => ({ count: match[1], tier: match[2] }),
  },
  {
    pattern: /Organization already has a subscription/i,
    key: 'backendErrors.alreadyHasSubscription',
  },
  {
    // Matches: "Module already subscribed: VENTA"
    pattern: /Module (?:.*)?already subscribed/i,
    key: 'backendErrors.moduleAlreadySubscribed',
  },
  {
    pattern: /Cannot change tier for inactive/i,
    key: 'backendErrors.cannotChangeTierInactive',
  },
  {
    pattern: /Cannot remove module.*depend/i,
    key: 'backendErrors.cannotRemoveDependency',
  },
  {
    // Matches: "Module VENTA is not a per-user module"
    pattern: /not a per-user module/i,
    key: 'backendErrors.notPerUserModule',
  },
  {
    pattern: /Module is not on trial|not on trial/i,
    key: 'backendErrors.moduleNotOnTrial',
  },
  {
    // Matches both "Missing required dependencies" and "Missing required dependency"
    pattern: /Missing required dependenc/i,
    key: 'backendErrors.missingDependencies',
  },
  {
    // Matches: "Cannot remove module: unpaid invoice INV_2026_000007 exists for this module..."
    pattern: /unpaid invoice.*exists for this module/i,
    key: 'backendErrors.cannotRemoveUnpaidInvoice',
  },
  {
    // Matches: "Tier already exists: VENTA/PRO"
    pattern: /Tier already exists/i,
    key: 'backendErrors.tierAlreadyExists',
  },
  {
    // Matches: "Dependency already exists: VENTA -> CORE"
    pattern: /Dependency already exists/i,
    key: 'backendErrors.dependencyAlreadyExists',
  },
  {
    // Matches: "Dependency not found: VENTA -> CORE"
    pattern: /Dependency not found/i,
    key: 'backendErrors.dependencyNotFound',
  },

  // =========================================================================
  // Registration/User errors
  // =========================================================================
  {
    pattern: /Email already has a pending registration/i,
    key: 'backendErrors.emailPendingRegistration',
  },
  {
    pattern: /User with this email already exists in the organization/i,
    key: 'backendErrors.userEmailExistsInOrg',
  },
  {
    pattern: /A user with this email already exists/i,
    key: 'backendErrors.userEmailExists',
  },
  {
    pattern: /Bootstrap is not allowed.*SuperAdmin already exists/i,
    key: 'backendErrors.superAdminExists',
  },

  // =========================================================================
  // Partner/Invite errors
  // =========================================================================
  {
    pattern: /Partner code already exists/i,
    key: 'backendErrors.partnerCodeExists',
  },
  {
    pattern: /Partner email already exists/i,
    key: 'backendErrors.partnerEmailExists',
  },
  {
    pattern: /Invite code already exists/i,
    key: 'backendErrors.inviteCodeExists',
  },

  // =========================================================================
  // Plan/Setting errors
  // =========================================================================
  {
    pattern: /Plan code already exists/i,
    key: 'backendErrors.planCodeExists',
  },
  {
    pattern: /Setting already exists/i,
    key: 'backendErrors.settingExists',
  },

  // =========================================================================
  // Payment/Billing errors
  // =========================================================================
  {
    pattern: /A payment with this invoice number already exists/i,
    key: 'backendErrors.invoiceExists',
  },
  {
    pattern: /A withdrawal with this reference number already exists/i,
    key: 'backendErrors.withdrawalRefExists',
  },

  // =========================================================================
  // Organization/Currency errors
  // =========================================================================
  {
    // Matches: "Cannot change organization currency from XOF to GNF. Financial transactions already exist..."
    pattern: /Cannot change organization currency.*Financial transactions already exist/i,
    key: 'backendErrors.cannotChangeCurrencyTransactionsExist',
  },

  // =========================================================================
  // Generic resource errors (keep at end - more specific patterns above)
  // =========================================================================
  {
    // Matches: "Organization not found", "User not found", "Payment not found", etc.
    pattern: /(\w+) not found/i,
    key: 'backendErrors.resourceNotFound',
    extractParams: (match) => ({ resource: match[1] }),
  },
  {
    // Generic "already exists" - catches any remaining patterns
    pattern: /already exists/i,
    key: 'backendErrors.alreadyExists',
  },

  // =========================================================================
  // Validation errors
  // =========================================================================
  {
    pattern: /must be at least (\d+)/i,
    key: 'backendErrors.mustBeAtLeast',
    extractParams: (match) => ({ min: match[1] }),
  },
  {
    pattern: /must not exceed (\d+)/i,
    key: 'backendErrors.mustNotExceed',
    extractParams: (match) => ({ max: match[1] }),
  },
  {
    pattern: /invalid.*format/i,
    key: 'backendErrors.invalidFormat',
  },

  // =========================================================================
  // Account status errors
  // =========================================================================
  {
    pattern: /Account.*locked/i,
    key: 'backendErrors.accountLocked',
  },
  {
    pattern: /Account.*suspended/i,
    key: 'backendErrors.accountSuspended',
  },
  {
    pattern: /Account.*deactivated/i,
    key: 'backendErrors.accountDeactivated',
  },

  // =========================================================================
  // Authentication errors
  // =========================================================================
  {
    pattern: /Invalid credentials|Invalid username or password/i,
    key: 'backendErrors.invalidCredentials',
  },
  {
    pattern: /Session expired/i,
    key: 'backendErrors.sessionExpired',
  },
  {
    pattern: /Token.*invalid|Invalid.*token/i,
    key: 'backendErrors.tokenInvalid',
  },
  {
    pattern: /Token.*expired/i,
    key: 'backendErrors.tokenExpired',
  },
  // =========================================================================
  // VENTA product import
  // =========================================================================
  {
    // Matches: "No default location to receive imported stock. Create a location ..."
    pattern: /No default location to receive imported stock/i,
    key: 'backendErrors.importNoStockLocation',
  },
];

/**
 * Try to match backend error message to a translation key
 */
export function matchBackendError(
  message: string,
  t?: TFunction
): string | null {
  if (!t || !message) return null;

  for (const { pattern, key, extractParams } of BACKEND_ERROR_PATTERNS) {
    const match = message.match(pattern);
    if (match) {
      const params = extractParams ? extractParams(match) : undefined;
      const translated = t(key, params);
      // If translation key is returned as-is, translation doesn't exist
      if (translated !== key) {
        return translated;
      }
    }
  }

  return null;
}

/**
 * Known error codes and their user-friendly messages
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // Authentication errors
  INVALID_CREDENTIALS: 'Invalid username or password.',
  ACCOUNT_LOCKED: 'Your account has been locked. Please contact support.',
  ACCOUNT_DISABLED: 'Your account has been disabled.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  TOKEN_INVALID: 'Invalid authentication token.',
  TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
  REFRESH_TOKEN_INVALID: 'Unable to refresh session. Please log in again.',

  // MFA errors
  MFA_REQUIRED: 'Multi-factor authentication is required.',
  MFA_INVALID_CODE: 'Invalid verification code. Please try again.',
  MFA_CODE_EXPIRED: 'Verification code has expired. Please request a new one.',
  MFA_SETUP_REQUIRED: 'Please set up multi-factor authentication to continue.',

  // Password errors
  PASSWORD_WEAK: 'Password does not meet security requirements.',
  PASSWORD_REUSED: 'Cannot reuse a recent password.',
  PASSWORD_RESET_TOKEN_INVALID: 'Password reset link is invalid or has expired.',
  PASSWORD_RESET_TOKEN_EXPIRED: 'Password reset link has expired. Please request a new one.',

  // Authorization errors
  ACCESS_DENIED: 'You do not have permission to perform this action.',
  INSUFFICIENT_PERMISSIONS: 'Insufficient permissions for this operation.',
  ROLE_REQUIRED: 'This action requires elevated privileges.',

  // Resource errors
  NOT_FOUND: 'The requested resource was not found.',
  ALREADY_EXISTS: 'A resource with this identifier already exists.',
  CONFLICT: 'This operation conflicts with existing data.',

  // Database constraint errors
  DUPLICATE_ENTRY: 'This record already exists.',
  DATA_INTEGRITY_VIOLATION: 'The operation could not be completed due to a data conflict.',
  REFERENCE_VIOLATION: 'This record is referenced by other data and cannot be modified.',

  // Validation errors
  VALIDATION_ERROR: 'Please check your input and try again.',
  INVALID_FORMAT: 'Invalid data format provided.',
  REQUIRED_FIELD_MISSING: 'Required fields are missing.',

  // Rate limiting
  RATE_LIMITED: 'Too many requests. Please wait a moment and try again.',
  RATE_LIMIT_EXCEEDED: 'Too many requests. Please wait a moment and try again.',

  // Server errors
  INTERNAL_ERROR: 'An unexpected error occurred. Please try again later.',
  SERVICE_UNAVAILABLE: 'Service is temporarily unavailable. Please try again later.',

  // Network errors
  NETWORK_ERROR: 'Unable to connect to server. Please check your internet connection.',
  TIMEOUT: 'Request timed out. Please try again.',

  // Default
  UNKNOWN_ERROR: 'An unexpected error occurred.',
};

/**
 * Get user-friendly error message from error code
 */
export function getErrorMessage(code: string): string {
  return ERROR_MESSAGES[code] || ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Check if error is an API error
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'status' in error
  );
}

/**
 * Extract validation errors from API error (supports both fieldErrors and details)
 */
export function getValidationErrors(error: ApiError): Record<string, string> | null {
  // Prefer direct fieldErrors (new format)
  if (error.fieldErrors && Object.keys(error.fieldErrors).length > 0) {
    return error.fieldErrors;
  }

  // Fallback to details (legacy format)
  if (!error.details) return null;

  const validationErrors: Record<string, string> = {};

  for (const [field, messages] of Object.entries(error.details)) {
    validationErrors[field] = messages[0]; // Take first error message for each field
  }

  return validationErrors;
}

/**
 * Check if error is a duplicate/constraint error
 */
export function isDuplicateError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.status === 409 || [
      'DUPLICATE_ENTRY',
      'DATA_INTEGRITY_VIOLATION',
      'ALREADY_EXISTS',
      'CONFLICT',
    ].includes(error.code);
  }
  return false;
}

/**
 * Check if error is a reference/foreign key violation
 */
export function isReferenceError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.status === 422 || error.code === 'REFERENCE_VIOLATION';
  }
  return false;
}

/**
 * Check if error has field-level errors
 */
export function hasFieldErrors(error: unknown): boolean {
  if (isApiError(error)) {
    const hasFieldErrs = error.fieldErrors && Object.keys(error.fieldErrors).length > 0;
    const hasDetails = error.details && Object.keys(error.details).length > 0;
    return Boolean(hasFieldErrs || hasDetails);
  }
  return false;
}

/**
 * Format error for display in toast/notification
 * @param error - The error object
 * @param t - Optional translation function for i18n support
 */
export function formatErrorForDisplay(
  error: unknown,
  t?: TFunction
): { title: string; message: string } {
  if (isApiError(error)) {
    // Try to match and translate backend error message
    const translatedMessage = matchBackendError(error.message, t);
    if (translatedMessage) {
      return {
        title: t ? t('common.error', { defaultValue: 'Error' }) : 'Error',
        message: translatedMessage,
      };
    }

    // Fallback to original message or code-based message
    const message = getErrorMessage(error.code);
    return {
      title: t ? t('common.error', { defaultValue: 'Error' }) : 'Error',
      message: error.message || message,
    };
  }

  if (error instanceof Error) {
    // Try to match and translate error message
    const translatedMessage = matchBackendError(error.message, t);
    return {
      title: t ? t('common.error', { defaultValue: 'Error' }) : 'Error',
      message: translatedMessage || error.message,
    };
  }

  return {
    title: t ? t('common.error', { defaultValue: 'Error' }) : 'Error',
    message: t ? t('backendErrors.unknownError', { defaultValue: ERROR_MESSAGES.UNKNOWN_ERROR }) : ERROR_MESSAGES.UNKNOWN_ERROR,
  };
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.code === 'NETWORK_ERROR' || error.status === 0;
  }
  return false;
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.status === 401 || [
      'INVALID_CREDENTIALS',
      'SESSION_EXPIRED',
      'TOKEN_INVALID',
      'TOKEN_EXPIRED',
    ].includes(error.code);
  }
  return false;
}

/**
 * Check if error is a permission error
 */
export function isPermissionError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.status === 403 || [
      'ACCESS_DENIED',
      'INSUFFICIENT_PERMISSIONS',
      'ROLE_REQUIRED',
    ].includes(error.code);
  }
  return false;
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.status === 400 || error.code === 'VALIDATION_ERROR';
  }
  return false;
}

/**
 * Check if error is a rate limit error
 */
export function isRateLimitError(error: unknown): boolean {
  if (isApiError(error)) {
    return error.status === 429 || error.code === 'RATE_LIMITED' || error.code === 'RATE_LIMIT_EXCEEDED';
  }
  return false;
}

/**
 * Log error for debugging (non-sensitive info only)
 */
export function logError(error: unknown, context?: string): void {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[API Error]${context ? ` (${context})` : ''}:`, error);
  }
}

/**
 * Create a standardized error from any caught exception
 */
export function normalizeError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message,
      status: 500,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: ERROR_MESSAGES.UNKNOWN_ERROR,
    status: 500,
  };
}
